import type { operations } from '#/api/schema'
import { ITEMS_PER_PAGE } from '#/constants/pagination'
import { isDownloadableMedia } from '#/utils/eTicketMedia'
import { createEnumGuard } from '#/utils/typeGuards'
import type { TStatusGroup } from '#lib/statusGroups'

import type { IETicket, TDeliveryMethod, TETicketStatus } from './interfaces'
import { isETicketStatus } from './interfaces'

export const ETICKET_LIST_STATUSES = ['new', 'awaiting_media', 'complete', 'failed'] as const
export const ETICKET_DELIVERY_METHODS = ['homeprint', 'wallet'] as const
export const ETICKET_TRAVEL_PERIODS = ['next_7_days', 'this_month', 'past'] as const
export const ETICKET_SORT_FIELDS = ['tag', 'travelDate', 'deliveryMethod', 'price', 'status'] as const

export type TTravelPeriod = (typeof ETICKET_TRAVEL_PERIODS)[number]
export type TSortField = (typeof ETICKET_SORT_FIELDS)[number]
export type TSortDir = 'asc' | 'desc'

export const isTravelPeriod = createEnumGuard(ETICKET_TRAVEL_PERIODS)
export const isDeliveryMethod = createEnumGuard(ETICKET_DELIVERY_METHODS)
export const isSortField = createEnumGuard(ETICKET_SORT_FIELDS)

const STATUS_RANK: Record<string, number> = {
  failed: 0,
  new: 1,
  awaiting_media: 2,
  complete: 3,
}

export const ETICKET_STATUS_PRESENTATION: Record<
  TETicketStatus,
  { labelKey: 'Requested' | 'Processing' | 'Completed' | 'Failed'; variant: TStatusGroup }
> = {
  new: { labelKey: 'Requested', variant: 'info' },
  awaiting_media: { labelKey: 'Processing', variant: 'info' },
  complete: { labelKey: 'Completed', variant: 'success' },
  failed: { labelKey: 'Failed', variant: 'danger' },
}

export interface IETicketSearch {
  orderTicket?: boolean
  status?: TETicketStatus
  deliveryMethod?: TDeliveryMethod[]
  travelPeriod?: TTravelPeriod
  search?: string
  sortBy?: TSortField
  sortDir?: TSortDir
  page?: number
}

export interface IETicketListParams {
  status?: TETicketStatus
  deliveryMethods: TDeliveryMethod[]
  travelPeriod?: TTravelPeriod
  search: string
  sortBy?: TSortField
  sortDir: TSortDir
  page: number
}

export interface IRowActionState {
  visible: boolean
  enabled: boolean
  reason: string | null
}

/** Ticket counts per filter group. Every key is present; a key the stats response lacked counts 0. */
export interface ICounts {
  status: Record<TETicketStatus, number>
  deliveryMethod: Record<TDeliveryMethod, number>
  total: number
}

/** The counts endpoint's payload as received: a group or a key may be missing. */
export interface IPartialCounts {
  status?: Partial<Record<TETicketStatus, number | null>> | null
  deliveryMethod?: Partial<Record<TDeliveryMethod, number | null>> | null
  total?: number | null
}

type TListQuery = NonNullable<operations['e_ticket_list']['parameters']['query']>
type TCountsQuery = NonNullable<operations['getETicketCountsByCompany']['parameters']['query']>

const readString = (value: unknown): string | undefined =>
  typeof value === 'string' && value.length > 0 ? value : undefined

const readDeliveryMethods = (value: unknown): TDeliveryMethod[] | undefined => {
  const rawValues = Array.isArray(value) ? value : value === undefined ? [] : [value]
  const flattened = rawValues.flatMap((item): unknown[] => {
    if (typeof item !== 'string' || item.length === 0) return []
    if (!item.startsWith('[')) return [item]
    try {
      const parsed: unknown = JSON.parse(item)
      return Array.isArray(parsed) ? parsed : [item]
    } catch {
      return [item]
    }
  })

  const methods = [
    ...new Set(flattened.filter((item): item is TDeliveryMethod => typeof item === 'string' && isDeliveryMethod(item))),
  ]
  return methods.length > 0 ? methods : undefined
}

/** Drop unknown values and defaults so a filtered view round-trips through the URL. */
export const validateETicketSearch = (search: Record<string, unknown>): IETicketSearch => {
  const status = readString(search.status)
  const travelPeriod = readString(search.travelPeriod)
  const sortBy = readString(search.sortBy)
  const sortDir = search.sortDir === 'asc' || search.sortDir === 'desc' ? search.sortDir : undefined
  const pageNumber = typeof search.page === 'number' ? search.page : Number(readString(search.page))
  const knownSort = sortBy !== undefined && isSortField(sortBy) ? sortBy : undefined
  const isDefaultSort = (knownSort === undefined || knownSort === 'travelDate') && sortDir !== 'asc'

  return {
    orderTicket: search.orderTicket === true || search.orderTicket === 'true' ? true : undefined,
    status: status !== undefined && isETicketStatus(status) ? status : undefined,
    deliveryMethod: readDeliveryMethods(search.deliveryMethod),
    travelPeriod: travelPeriod !== undefined && isTravelPeriod(travelPeriod) ? travelPeriod : undefined,
    search: readString(search.search),
    sortBy: isDefaultSort ? undefined : knownSort,
    sortDir: isDefaultSort ? undefined : (sortDir ?? 'desc'),
    page: Number.isInteger(pageNumber) && pageNumber > 1 ? pageNumber : undefined,
  }
}

export const toListParams = (search: IETicketSearch): IETicketListParams => ({
  status: search.status,
  deliveryMethods: search.deliveryMethod ?? [],
  travelPeriod: search.travelPeriod,
  search: search.search ?? '',
  sortBy: search.sortBy,
  sortDir: search.sortDir ?? 'desc',
  page: (search.page ?? 1) - 1,
})

/** Query string fields for the company collection. Defaults stay off the request. */
export const buildETicketListQuery = (params: IETicketListParams): TListQuery => {
  const query: TListQuery = {
    page: params.page + 1,
    itemsPerPage: ITEMS_PER_PAGE,
  }
  if (params.status) query.status = params.status
  if (params.deliveryMethods.length > 0) query['deliveryMethod[]'] = params.deliveryMethods
  if (params.travelPeriod) query.travelPeriod = params.travelPeriod
  if (params.search) query.search = params.search
  if (params.sortBy) {
    query.orderBy = params.sortBy
    query.orderDirection = params.sortDir === 'asc' ? 'ASC' : 'DESC'
  }
  return query
}

export const buildETicketCountsQuery = (
  params: Pick<IETicketListParams, 'status' | 'deliveryMethods' | 'travelPeriod' | 'search'>,
): TCountsQuery => {
  const query: TCountsQuery = {}
  if (params.status) query.status = params.status
  if (params.deliveryMethods.length > 0) query['deliveryMethod[]'] = params.deliveryMethods
  if (params.travelPeriod) query.travelPeriod = params.travelPeriod
  if (params.search) query.search = params.search
  return query
}

export const amsterdamToday = (now: Date = new Date()): string =>
  new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Amsterdam',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(now)

const addIsoDays = (isoDate: string, days: number): string => {
  const [year, month, day] = isoDate.split('-').map(Number)
  const date = new Date(Date.UTC(year, month - 1, day))
  date.setUTCDate(date.getUTCDate() + days)
  return date.toISOString().slice(0, 10)
}

export const matchesTravelPeriod = (travelDate: string, period: TTravelPeriod | undefined, today: string): boolean => {
  if (!period) return true
  if (period === 'past') return travelDate < today
  if (period === 'next_7_days') return travelDate >= today && travelDate <= addIsoDays(today, 7)
  const [year, month] = today.split('-')
  return travelDate.startsWith(`${year}-${month}-`)
}

export const searchTerms = (search: string): string[] =>
  search
    .trim()
    .split(/\s+/u)
    .map((term) => term.toLowerCase())
    .filter((term) => term.length >= 2)

const matchesTerms = (ticket: IETicket, terms: string[]): boolean => {
  if (terms.length === 0) return true
  const fields = [ticket.tag, ticket.travellerEmail, ticket.fromStationName, ticket.toStationName].map((value) =>
    value.toLowerCase(),
  )
  return terms.every((term) => fields.some((field) => field.includes(term)))
}

const compareTickets = (left: IETicket, right: IETicket, sortBy: TSortField | undefined, sortDir: TSortDir): number => {
  const direction = sortDir === 'asc' ? 1 : -1
  const field = sortBy ?? 'travelDate'
  let difference = 0
  if (field === 'status') difference = (STATUS_RANK[left.status] ?? 3) - (STATUS_RANK[right.status] ?? 3)
  else if (field === 'price') difference = (left.price ?? 0) - (right.price ?? 0)
  else if (field === 'tag') difference = left.tag.localeCompare(right.tag)
  else if (field === 'deliveryMethod') difference = left.deliveryMethod.localeCompare(right.deliveryMethod)
  else difference = left.travelDate.localeCompare(right.travelDate)

  if (difference !== 0) return difference * direction
  const created = (right.createdAt ?? '').localeCompare(left.createdAt ?? '')
  if (created !== 0) return created
  return right.id.localeCompare(left.id)
}

const matchesFilters = (
  ticket: IETicket,
  params: Pick<IETicketListParams, 'status' | 'deliveryMethods' | 'travelPeriod' | 'search'>,
  today: string,
  ignore: 'status' | 'delivery' | 'none',
): boolean => {
  if (ignore !== 'status' && params.status && ticket.status !== params.status) return false
  if (
    ignore !== 'delivery' &&
    params.deliveryMethods.length > 0 &&
    !params.deliveryMethods.includes(ticket.deliveryMethod)
  )
    return false

  if (!matchesTravelPeriod(ticket.travelDate, params.travelPeriod, today)) return false
  return matchesTerms(ticket, searchTerms(params.search))
}

export const filterMockETickets = (
  tickets: readonly IETicket[],
  params: IETicketListParams,
  today = amsterdamToday(),
): IETicket[] => {
  const matched = tickets.filter((ticket) => matchesFilters(ticket, params, today, 'none'))
  return [...matched].sort((left, right) => compareTickets(left, right, params.sortBy, params.sortDir))
}

const emptyStatus = (): Record<TETicketStatus, number> => ({ new: 0, awaiting_media: 0, complete: 0, failed: 0 })
const emptyDelivery = (): Record<TDeliveryMethod, number> => ({ homeprint: 0, wallet: 0 })

const readCount = (value: unknown): number => (typeof value === 'number' && Number.isFinite(value) ? value : 0)

/** Parse boundary for the counts endpoint: a missing group or key becomes 0 instead of a crash downstream. */
export const normalizeETicketCounts = (raw: IPartialCounts | null | undefined): ICounts => {
  const status = emptyStatus()
  const deliveryMethod = emptyDelivery()
  for (const key of ETICKET_LIST_STATUSES) status[key] = readCount(raw?.status?.[key])
  for (const key of ETICKET_DELIVERY_METHODS) deliveryMethod[key] = readCount(raw?.deliveryMethod?.[key])
  return { status, deliveryMethod, total: readCount(raw?.total) }
}

export const countMockETickets = (
  tickets: readonly IETicket[],
  params: Pick<IETicketListParams, 'status' | 'deliveryMethods' | 'travelPeriod' | 'search'>,
  today = amsterdamToday(),
): ICounts => {
  const status = emptyStatus()
  const deliveryMethod = emptyDelivery()
  for (const ticket of tickets) {
    if (matchesFilters(ticket, params, today, 'status') && isETicketStatus(ticket.status)) status[ticket.status] += 1

    if (matchesFilters(ticket, params, today, 'delivery')) deliveryMethod[ticket.deliveryMethod] += 1
  }
  return { status, deliveryMethod, total: tickets.length }
}

export const formatJourneyDate = (travelDate: string, locale: string): string => {
  const [year, month, day] = travelDate.split('-').map(Number)
  return new Intl.DateTimeFormat(locale, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(Date.UTC(year, month - 1, day, 12)))
}

export const isTravelDatePast = (travelDate: string, today = amsterdamToday()): boolean => travelDate < today

const NO_FILE = 'No file available yet'
const FAILED = 'Ordering failed — there is no file'
const PASSED = 'Travel date has passed'

const sharedActionBlock = (status: string, travelDate: string, today: string): IRowActionState => {
  if (status === 'new' || status === 'awaiting_media') return { visible: true, enabled: false, reason: NO_FILE }
  if (status === 'failed') return { visible: true, enabled: false, reason: FAILED }
  if (status !== 'complete') return { visible: true, enabled: false, reason: NO_FILE }
  if (isTravelDatePast(travelDate, today)) return { visible: true, enabled: false, reason: PASSED }
  return { visible: true, enabled: true, reason: null }
}

export const getRowActionState = (
  row: Pick<IETicket, 'status' | 'deliveryMethod' | 'travelDate' | 'media'>,
  today = amsterdamToday(),
): { download: IRowActionState; resend: IRowActionState } => {
  const shared = sharedActionBlock(row.status, row.travelDate, today)
  if (row.deliveryMethod === 'wallet')
    return { download: { visible: false, enabled: false, reason: null }, resend: shared }

  if (shared.enabled && !row.media.some(isDownloadableMedia))
    return { download: { visible: true, enabled: false, reason: NO_FILE }, resend: shared }

  return { download: shared, resend: shared }
}
